import React from "react";
import { useTranslation } from "react-i18next";
import { EmteriaRoles } from "src/data/roles";
import i18next from "src/i18n/config";
import { withRequiredRole } from "src/ui/navigation/utils/withRequiredRole";
import { HoverRow } from "src/ui/shared/table/HoverRow";

import { deleteWorkspace } from "@admin/api/workspace";
import { useGetAllWorkspaces } from "@admin/hooks/useGetAllWorkspaces";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import PageHeader from "@dashboard/shared/components/PageHeader";
import { ConfirmDialog } from "@devices/components/shared";
import { Box, DialogContentText, TableRow, Typography } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { useFullPageTableLayoutStyles } from "@shared/table/FullPageTableLayout.style";
import { MinimalTable, MinimalTableContainer, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import { pageIndexOffset } from "@shared/table/Tanstack.utils";
import { useContainerOverflow } from "@shared/table/useContainerOverflow";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import {
    ColumnFiltersState,
    flexRender,
    functionalUpdate,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    Updater,
    useReactTable,
} from "@tanstack/react-table";

import { getWorkspaceColumns } from "./AdminWorkspacesPage.utils";
import EditWorkspaceModal from "./EditWorkspaceModal";

type DeleteState = {
    id: number | null;
    open: boolean;
};

const AdminWorkspacesPage = () => {
    const [pageIndex, setPageIndex] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(12);
    const [editingWorkspace, setEditingWorkspace] = React.useState<API.Workspace | null>(null);
    const [sorting, setSorting] = React.useState<{ id: string; desc: boolean } | null>(null);
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
    const [deleteState, setDeleteState] = React.useState<DeleteState>({ id: null, open: false });
    const [isDeleting, setIsDeleting] = React.useState(false);

    const { data, isLoading, removeWorkspace, updateWorkspace } = useGetAllWorkspaces({
        pageSize: rowsPerPage,
        pageIndex: pageIndex + pageIndexOffset,
        sortCriteria: sorting?.id,
        sortDirection: sorting?.desc ? "descending" : "ascending",
    });

    const { classes: layoutClasses, cx } = useFullPageTableLayoutStyles();
    const { t } = useTranslation(["pages", "modals", "common"]);

    const handleEditWorkspace = (workspace: API.Workspace) => {
        setEditingWorkspace(workspace);
    };

    const handleCloseEditModal = () => {
        setEditingWorkspace(null);
    };

    const handleWorkspaceUpdated = (updatedWorkspace: API.Workspace) => {
        updateWorkspace(updatedWorkspace);
        setEditingWorkspace(null);
    };

    const handleDeleteWorkspace = (id: number) => {
        setDeleteState({ id, open: true });
    };

    const onDelete = async () => {
        if (deleteState.id) {
            setIsDeleting(true);
            try {
                await deleteWorkspace(deleteState.id);
                showSuccessToast(t("pages:adminWorkspacePage.toast.workspaceDeletedSuccess"));
                removeWorkspace(deleteState.id);
            } catch (error) {
                showErrorToast(
                    error instanceof Error ? error.message : t("pages:adminWorkspacePage.toast.workspaceDeletedError"),
                );
            } finally {
                setIsDeleting(false);
                setDeleteState({ id: null, open: false });
            }
        }
    };

    const onSortingChange = (updater: Updater<SortingState>) => {
        const newValue = functionalUpdate(updater, sorting ? [sorting] : []);
        if (newValue.length > 0) {
            setSorting(newValue[0]);
        } else {
            setSorting(null);
        }
        setPageIndex(0);
    };

    const workspaces = data?.list || [];
    const totalCount = data?.count || 0;
    const columns = getWorkspaceColumns({
        onEdit: handleEditWorkspace,
        onDelete: handleDeleteWorkspace,
        deletingId: isDeleting ? deleteState.id : null,
    });

    const table = useReactTable({
        data: workspaces,
        columns,
        state: { columnFilters, sorting: sorting ? [sorting] : [] },
        initialState: {
            pagination: {
                pageSize: rowsPerPage,
                pageIndex: 0,
            },
        },
        onColumnFiltersChange: setColumnFilters,
        onSortingChange: onSortingChange,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        manualPagination: true,
        manualSorting: true,
        pageCount: Math.ceil(totalCount / rowsPerPage),
    });

    const { pageSize } = table.getState().pagination;

    const { ref: tableContainerRef, contentOverflows } = useContainerOverflow(true, [pageIndex, rowsPerPage, workspaces.length]);

    return (
        <PageContainer fitViewport>
            <PageHeader title={t("pages:adminWorkspaceage.title")} description={t("pages:adminWorkspaceage.description")} />
            <Box className={layoutClasses.tableLayout}>
                <Box className={layoutClasses.tableWrapper}>
                    <MinimalTableContainer
                        ref={tableContainerRef}
                        className={cx(layoutClasses.tableContainer, !contentOverflows && layoutClasses.tableContainerNoScroll)}
                    >
                        <MinimalTable>
                            <MinimalTableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <Header key={header.id} {...header} />
                                        ))}
                                    </TableRow>
                                ))}
                            </MinimalTableHeader>
                            <TableBody
                                table={table}
                                loading={isLoading}
                                noDataMessage={t("pages:adminWorkspaceage.noWorkspacesFound")}
                            >
                                {table.getRowModel().rows.map((row) => (
                                    <HoverRow<API.Workspace> key={row.id} {...row}>
                                        {row.getVisibleCells().map((cell) =>
                                            flexRender(cell.column.columnDef.cell, {
                                                ...cell.getContext(),
                                                key: cell.id,
                                            }),
                                        )}
                                    </HoverRow>
                                ))}
                            </TableBody>
                        </MinimalTable>
                    </MinimalTableContainer>
                </Box>
                <CustomTablePagination
                    component="div"
                    className={layoutClasses.pagination}
                    rowsPerPageOptions={[12, 24, 48]}
                    count={totalCount}
                    rowsPerPage={pageSize}
                    page={pageIndex}
                    onPageChange={(_event, page) => {
                        setPageIndex(page);
                        table.setPageIndex(page);
                    }}
                    onRowsPerPageChange={(e) => {
                        const size = e.target.value ? Number(e.target.value) : 12;
                        setRowsPerPage(size);
                        setPageIndex(0);
                        table.setPageSize(size);
                        table.setPageIndex(0);
                    }}
                />
            </Box>
            {editingWorkspace && (
                <EditWorkspaceModal
                    open={true}
                    workspace={editingWorkspace}
                    onClose={handleCloseEditModal}
                    onSuccess={handleWorkspaceUpdated}
                />
            )}
            <ConfirmDialog
                title={t("modals:adminDeleteWorkspace.title")}
                dangerButton
                primaryActionText={t("common:button.delete")}
                open={deleteState.open}
                onConfirm={onDelete}
                onClose={() => setDeleteState({ id: null, open: false })}
            >
                <DialogContentText color="text.primary">{t("modals:adminDeleteWorkspace.description")}</DialogContentText>
                <ul style={{ paddingLeft: 24, marginTop: 8 }}>
                    <li>
                        <Typography color="text.primary">{t("modals:adminDeleteWorkspace.warnings.products")}</Typography>
                    </li>
                    <li>
                        <Typography color="text.primary">{t("modals:adminDeleteWorkspace.warnings.files")}</Typography>
                    </li>
                    <li>
                        <Typography color="text.primary">{t("modals:adminDeleteWorkspace.warnings.groups")}</Typography>
                    </li>
                    <li>
                        <Typography color="text.primary">{t("modals:adminDeleteWorkspace.warnings.provisionings")}</Typography>
                    </li>
                    <li>
                        <Typography color="text.primary">{t("modals:adminDeleteWorkspace.warnings.licenses")}</Typography>
                    </li>
                    <li>
                        <Typography color="text.primary">{t("modals:adminDeleteWorkspace.warnings.members")}</Typography>
                    </li>
                    <li>
                        <Typography color="text.primary">{t("modals:adminDeleteWorkspace.warnings.workspace")}</Typography>
                    </li>
                </ul>
            </ConfirmDialog>
        </PageContainer>
    );
};

export default withRequiredRole(
    withMetaDescription(AdminWorkspacesPage, {
        title: i18next.t("pages:adminWorkspaceage.metaTitle"),
        description: i18next.t("pages:adminWorkspaceage.metaDescription"),
    }),
    [EmteriaRoles.EmteriaSalesRole, EmteriaRoles.EmteriaAccountantRole],
);
